import { StepsTrack } from '@/components/atoms/StepsTrack/StepsTrack'
import { Box, Flex, Icon } from '@chakra-ui/react'
import React, { memo } from 'react'
import { useCoinflipStore, UserCoice } from './CoinflipProvider'
import { GAME_MODE } from './types'
import CoinHeadIcon from '@/public/assets/images/coinflip/coin-head.svg'
import CoinTailsIcon from '@/public/assets/images/coinflip/coin-tail.svg'
import { CoinflipChoice, MiniGameStatus } from '@/__generated__/graphql'
import { AnimatePresence, motion } from 'framer-motion'
import { StepBadge, StepCoinBadge } from './StepBadge'
import { useStoreWithEqualityFn } from 'zustand/traditional'
import { useShallow } from 'zustand/react/shallow'
import { getTokenByDenom } from '@/services/token-service'
import { TokenMoney } from '@ninjablaze/sdk-ts'

export const MultipliersTrack = memo(() => {
  const roundState = useCoinflipStore((state) => state.view.roundState)
  const history = useCoinflipStore((state) => state.view.history)

  const mode = useCoinflipStore(useShallow((state) => state.form.fields.mode))
  const denom = useCoinflipStore(useShallow((state) => state.form.fields.denom))

  const steps = useStoreWithEqualityFn(
    useCoinflipStore,
    (state) => state.gameSteps,
    (a, b) => {
      return a.length == b.length && a.at(-1)!.payable.isEqualTo(b.at(-1)!.payable)
    }
  )

  if (mode === GAME_MODE.INSTANT) {
    return (
      <Flex
        px={{ base: 4, md: 0 }}
        alignItems={'center'}
        h={{ base: '28px', md: '44px' }}
        mb={{ base: 3, md: 6 }}
        gap={{ base: 2, md: 4 }}
      >
        <AnimatePresence initial={false}>
          {history.map((coin) => (
            <LastResult key={coin.id} coin={coin} />
          ))}
        </AnimatePresence>
      </Flex>
    )
  }

  const token = getTokenByDenom(denom)

  return (
    <Box mb={{ base: 3, md: 6 }} px={{ base: 4, md: 0 }}>
      <StepsTrack
        wrapperStyles={{
          h: { base: '28px', md: '44px' },
          gap: 2.5,
        }}
        arrowsStyles={{
          px: 1.5,
          bg: 'rgba(255, 255, 255, 0.08)',
        }}
        step={1}
      >
        {steps.slice(1, steps.length).map((x, index) => {
          return !x.isLast ? (
            <StepBadge key={index} coin={roundState.userChoices[index]?.choice} multiplier={x.multiplier} />
          ) : (
            <StepCoinBadge
              key={index}
              icon={token.iconMono(4)}
              coin={roundState.userChoices[index]?.choice}
              amount={new TokenMoney(x.payable, token.denom).humanFormat}
            />
          )
        })}
      </StepsTrack>
    </Box>
  )
})

const LastResult = memo(
  ({
    coin,
  }: {
    coin: UserCoice & {
      id: string
    }
  }) => {
    return (
      <motion.div
        key={coin.id}
        layout
        initial={{ x: -50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 50, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
      >
        {coin.choice === CoinflipChoice.Heads ? (
          <Icon
            transition={'0.2s'}
            opacity={coin.result === MiniGameStatus.Win ? 1 : 0.6}
            h={{ base: '28px', md: 9 }}
            w={{ base: '28px', md: 9 }}
            as={CoinHeadIcon}
          />
        ) : (
          <Icon
            transition={'0.2s'}
            opacity={coin.result === MiniGameStatus.Win ? 1 : 0.6}
            h={{ base: '28px', md: 9 }}
            w={{ base: '28px', md: 9 }}
            as={CoinTailsIcon}
          />
        )}
      </motion.div>
    )
  }
)
